import React, { useEffect, useLayoutEffect, useState, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import App from './App';
import CalumPopup from './components/CalumPopup';
import AndroidInstallBar from './components/AndroidInstallBar';
import { AnalyticsConsent } from './components/AnalyticsConsent';
import { measurePage, trackOutboundClick } from './utils/analytics';
import './index.css';

const HelpDeskWidget = lazy(() => import('./components/HelpDeskWidget'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const BlogCategoryPage = lazy(() => import('./pages/BlogCategoryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const CliniciansPage = lazy(() => import('./pages/CliniciansPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const ContentSchedulerPage = lazy(() => import('./pages/ContentSchedulerPage'));
const PortalsPage = lazy(() => import('./pages/PortalsPage'));
const ConditionRoute = lazy(() => import('./pages/ConditionRoute'));

if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
window.addEventListener('beforeinstallprompt', (e) => e.preventDefault());

function RouteEffects() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  useEffect(() => { measurePage(); }, [pathname]);
  useEffect(() => {
    document.addEventListener('click', trackOutboundClick, true);
    return () => document.removeEventListener('click', trackOutboundClick, true);
  }, []);
  // The readable build-time document stays available until the real page has mounted.
  // It is not a React snapshot: don't hydrate mismatching markup or hide it from non-JS users.
  useEffect(() => {
    const fallback = document.getElementById('static-content');
    if (!fallback) return;
    const root = document.getElementById('root');
    const swap = () => {
      if (!root.querySelector('h1')) return;
      fallback.remove();
      root.removeAttribute('data-awaiting-page');
      observer.disconnect();
    };
    const observer = new MutationObserver(swap);
    observer.observe(root, { childList: true, subtree: true });
    swap();
    return () => observer.disconnect();
  }, []);
  return null;
}

function DeferredWidgets() {
  const [ready, setReady] = useState(false);
  useEffect(() => { const timer = setTimeout(() => setReady(true), 8000); return () => clearTimeout(timer); }, []);
  useEffect(() => {
    const open = () => { window.__rcOpenHelpdesk = true; setReady(true); };
    window.addEventListener('recalibrate:open-helpdesk', open);
    return () => window.removeEventListener('recalibrate:open-helpdesk', open);
  }, []);
  return <><CalumPopup /><AndroidInstallBar />{ready && <Suspense fallback={null}><HelpDeskWidget /></Suspense>}</>;
}

const AppRoutes = () => (
  <HelmetProvider><BrowserRouter>
    <RouteEffects />
    <Suspense fallback={<div data-testid="route-loading" className="min-h-[70vh] flex items-center justify-center" role="status">Loading…</div>}>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/start" element={<Navigate to="/features" replace />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/clinicians" element={<CliniciansPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/:slug" element={<ProductPage />} />
        <Route path="/content-scheduler" element={<ContentSchedulerPage />} />
        <Route path="/portals" element={<PortalsPage />} />
        <Route path="/resources" element={<Navigate to="/#discover" replace />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/topic/:topic" element={<BlogCategoryPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/recalibrate101" element={<Navigate to="/#starter-kit" replace />} />
        <Route path="/course" element={<Navigate to="/#starter-kit" replace />} />
        <Route path="/:slug" element={<ConditionRoute />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    <DeferredWidgets /><AnalyticsConsent /><Toaster position="top-center" />
  </BrowserRouter></HelmetProvider>
);

ReactDOM.createRoot(document.getElementById('root')).render(<React.StrictMode><AppRoutes /></React.StrictMode>);